import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'

import type { Program } from '@policyfly/protobuf/pfbingo'

export const useProgramStore = defineStore('program', () => {
  const programsRef = shallowRef<Program[]>([])

  const loading = ref(true)
  const apiStore = useApiStore()
  async function loadPrograms (): Promise<void> {
    if (programsRef.value.length) return
    loading.value = true
    const response = await apiStore.listPrograms({})
    programsRef.value = response.programs
    loading.value = false
  }

  const programs = computed(() => programsRef.value)

  // programs enrolled in the currently selected environment + domain
  const environmentStore = useEnvironmentStore()
  const domainStore = useDomainStore()
  const scopedProgramsRef = shallowRef<Program[]>([])
  const scopedProgramsLoading = ref(false)
  let scopedProgramsRequest = 0
  /**
   * Loads the programs enrolled in the current environment + domain.
   * Only programs in this list can have groups/users added to them by bingo.
   */
  async function loadScopedPrograms (): Promise<void> {
    const request = ++scopedProgramsRequest
    const environmentId = environmentStore.currentEnvironment?.id
    if (!environmentId) {
      scopedProgramsRef.value = []
      return
    }
    scopedProgramsLoading.value = true
    try {
      const response = await apiStore.listProgramsForEnvironment({
        environmentId,
        domain: domainStore.currentDomain,
      })
      // ignore stale responses when the environment/domain changed mid-request
      if (request !== scopedProgramsRequest) return
      scopedProgramsRef.value = response.programs
    } finally {
      if (request === scopedProgramsRequest) scopedProgramsLoading.value = false
    }
  }

  const scopedPrograms = computed(() => scopedProgramsRef.value)

  return {
    programs,
    loading,
    loadPrograms,
    scopedPrograms,
    scopedProgramsLoading,
    loadScopedPrograms,
  }
})
